import { OpenSansText } from "@Components/CustomText/OpenSansText"
import { InputGroup } from "@Components/Input/InputGroups"
import { Spacer } from "@Components/Space"
import React, { useContext, useEffect, useState } from "react"
import { TouchableOpacity, View } from "react-native"
import DateTimePicker from "@react-native-community/datetimepicker"
import { FreeTimeSelector } from "@Components/Input/FreeTimeSelector"
import {
    GreaterThanNowDate,
    capitalizeFirstLetter,
    dbDateFormat,
    dbDateTimeFormat,
    fullName,
    isValidEmail,
    isWithin48Hours,
    localDateFormat,
    removeDateTimeZonePlus
} from "@Utils/utils"
import { baseColors, dimensions } from "@Constants/baseStyles"
import { UserService } from "@Services/User.service"
import { useRecoilValue } from "recoil"
import { userAtom } from "@Recoil/Atom/userAtom"
import { ShowToast } from "@Components/ToastNotification/ToastNotification"
import { DiagnosisState, message } from "@Constants/constant"
import { ActivityIndicator, Divider } from "react-native-paper"
import { EventService } from "@Services/Event.Service"
import { Button } from "@Components/CustomButton/Button"
import { currentDiagnosisAtom } from "@Recoil/Atom/DiagnosisAtom"
import SvgVideoCall from "@Components/CustomIcon/SvgVideoCall"
import { BreeSerifText } from "@Components/CustomText/BreeSerifText"
import { useFetchUserInfos } from "../../Hooks"
import { defaultEvent, generateLiveKitUUID, useSurveyHandler } from "./Logic"
import { RFValue } from "react-native-responsive-fontsize"
import { useNavigation } from "@react-navigation/native"
import CustomEmptyData from "@Components/CustomEmptyData"
import { useUserPrograms } from "../../Hooks/useUserPrograms"
import { useFreeslotRQHooks } from "../../Hooks/ReactQueryHooks/useFreeslotRQHooks"
import { isNull } from "lodash"
import { UserProgramContext } from "@Providers/ProgramProvider"

const moment = require("moment")
moment.locale("fr")

export const VideoPoint = (props) => {
    const { isSimpleRdv, defaultRDV, eventType, disabled } = props
    const navigation = useNavigation()
    const { preselectedPatientProgram } = useContext(UserProgramContext)
    const { currentUserProgram, fetchUserPrograms } = useUserPrograms()
    const [date, setDate] = useState(new Date())
    const [loading, setLoading] = useState(true)
    const [showDatepicker, setShow] = useState(false)
    const [specialityTypes, setSpecialityTypes] = useState([])
    const [freeslots, setFreeslots] = useState([])
    const user = useRecoilValue(userAtom)
    const [activeSlot, setActiveSlot] = useState(null)
    const [rdvEvent, setRdvEvent] = useState(null)
    const currentEducationalDiagnosis = useRecoilValue(currentDiagnosisAtom)
    const iframeData =
        currentEducationalDiagnosis?.visioconferenceEvent?.visioconference ??
        currentEducationalDiagnosis?.visioconference ??
        ""
    const { fetchingUserInfos, userInfos, setUserInfos } = useFetchUserInfos()

    const [event, setEvent] = useState(defaultRDV ?? defaultEvent)
    const onChangeDateTimePicker = (event, selectedDate) => {
        const currentDate = selectedDate || date
        setShow(false)
        setDate(currentDate)
    }

    const fetchEventType = () => {
        EventService.getTypes()
            .then((response) => {
                const data = response.data?.data?.items

                if (data && data instanceof Array) {
                    let individual = data.filter((item) => item.name === eventType)

                    if (individual.length > 0) {
                        setRdvEvent(individual[0])
                        setEvent((old) => ({ ...old, typeId: individual[0]?.id }))
                    }
                }
            })
            .catch((e) => {
                ShowToast({
                    type: "error",
                    message: "Une erreur est survenue lors de la recupération des types d'événéments"
                })
            })
    }

    useEffect(() => {
        if (event.typeId && event.startDate && event.endDate && isValidEmail(event.customLocation)) {
            props.handleChange?.(event)
        }
    }, [event])

    useEffect(() => {
        if (activeSlot && activeSlot?.soignantId > 0) {
            const selectedCaregiver = activeSlot?.soignantId
            const rdvIntervenant = { id: user?.id, type: 1 }
            setEvent((old) => ({
                ...old,
                endDate: activeSlot?.end_date,
                startDate: activeSlot?.start_date,
                attendees: [{ ...old.attendees[0], id: parseInt(selectedCaregiver), type: 2 }],
                freeslotId: +activeSlot?.eventId,
                specialityTypeId: +activeSlot?.specialityId ?? 1,
                visioSessionId: activeSlot?.type === "VISIO" ? generateLiveKitUUID() : undefined
            }))
        }
    }, [activeSlot, user])

    const fetchSpecialityTypes = () => {
        setUserInfos(null)
        setLoading(true)
        UserService.getSpecialityTypes()
            .then((response) => {
                const data = response.data?.data?.items

                if (data) {
                    let parsed = data.map((d) => ({
                        label: d.name,
                        value: d.id,
                        id: d.id
                    }))

                    setSpecialityTypes(parsed)
                }
                setLoading(false)
            })
            .catch((e) => {
                setLoading(false)
            })
    }

    const { data, isLoading } = useFreeslotRQHooks()

    const fetchFreeslot = () => {
        if (data && data instanceof Array) {
            const items = data ?? []
            if (items?.length) {
                setFreeslots(items ?? [])
            }
        }
    }

    useEffect(() => {
        fetchFreeslot()
    }, [data])

    useEffect(() => {
        fetchSpecialityTypes()
        fetchEventType()
        handleFetchCalendar()
        setEvent((old) => ({ ...old, customLocation: user?.email }))
    }, [])

    const [calendarId, setCalendarId] = useState(1)
    const [openVisio, setOpenVisio] = useState(false)
    const [hasRdv, setHasRdv] = useState(false)
    const [rdvVisioDate, setRdvVisioDate] = useState(null)
    const [saving, setSaving] = useState(false)
    const [selectedFreeslot, setSelectedFreeslot] = useState(0)
    const toggleVisioModal = () => setOpenVisio(!openVisio)
    const [progressMessage, setProgressMessage] = useState("Création d'un nouveau diagnostic éducatif")
    const educationalDEId = currentEducationalDiagnosis?.id ?? 0

    useEffect(() => {
        if (currentEducationalDiagnosis?.visioconferenceEvent?.id > 0) {
            const visioStartDate = currentEducationalDiagnosis?.visioconferenceEvent?.startDate
            const visioEndDate = currentEducationalDiagnosis?.visioconferenceEvent?.endDate
            setRdvVisioDate({
                start: removeDateTimeZonePlus(visioStartDate),
                end: removeDateTimeZonePlus(visioEndDate)
            })
            if (currentEducationalDiagnosis?.diagnosisStatus?.code === DiagnosisState.PENDING) {
                setHasRdv(true)
            }
        }
        fetchUserPrograms()
    }, [currentEducationalDiagnosis])

    const joinRoom = () =>
        navigation.navigate("AppointmentVisioContent", {
            userDEUUID: currentEducationalDiagnosis?.visioconference?.uniqid,
            isDEVisio: true
        })

    const { performCreateEvent, handleFetchCalendar } = useSurveyHandler({
        educationalDEId,
        setSaving,
        setProgressMessage,
        event,
        navigation,
        user,
        currentUserProgram: currentUserProgram ?? {
            program: {
                id: preselectedPatientProgram?.[0]?.programId
            }
        },
        setCalendarId,
        calendarId
    })

    const disableBtn = !activeSlot || saving || event?.startDate === null
    const noFreeslotAvailable = freeslots?.length === 0
    const specialityName = activeSlot?.soignantId > 0 ? activeSlot?.speciality : null
    const consultationType = activeSlot?.soignantId > 0 ? activeSlot?.type : null
    const slotsTime = activeSlot?.soignantId > 0 ? activeSlot?.slots : null
    const enableVisioRdv = isWithin48Hours(rdvVisioDate?.end, rdvVisioDate?.start)
    const startDate = currentEducationalDiagnosis?.visioconferenceEvent?.startDate ?? new Date()
    const videoPointDate = currentEducationalDiagnosis?.visioconferenceEvent?.startDate
        ? moment(startDate, dbDateTimeFormat).format(localDateFormat + " à HH:mm")
        : null
    const showDetails = (startDate, soignantId) => {
        const value =
            moment(activeSlot?.start_date).format(dbDateFormat) === startDate && soignantId === activeSlot?.soignantId
        return value
    }

    const isPastDEVisio = GreaterThanNowDate(rdvVisioDate?.end)
    const needNewDERDV = isPastDEVisio && !enableVisioRdv

    console.log({ enableVisioRdv })

    return (
        <View style={{ padding: 10 }}>
            {hasRdv && !isSimpleRdv ? (
                <View style={{ marginBottom: 20 }}>
                    {isNull(videoPointDate) ? (
                        <View style={{ marginTop: dimensions.fullHeight / 6 }}>
                            <BreeSerifText style={{ textAlign: "center", color: baseColors.green, fontSize: 18 }}>
                                Pas de rendez-vous en attente.
                            </BreeSerifText>
                        </View>
                    ) : (
                        <>
                            <View style={{ marginTop: dimensions.fullHeight / 6 }}>
                                <SvgVideoCall style={{ top: 20, alignSelf: "center" }} height={100} width={100} />

                                <View style={{ alignItems: "center", marginTop: 50 }}>
                                    <BreeSerifText
                                        style={{ textAlign: "center", color: baseColors.green, fontSize: 18 }}
                                    >
                                        {!enableVisioRdv
                                            ? needNewDERDV
                                                ? `Le rendez-vous prévu le ${videoPointDate} est expiré.`
                                                : `Votre rendez-vous aura lieu le ${videoPointDate}`
                                            : "Vous avez un rendez-vous en attente."}
                                    </BreeSerifText>
                                </View>
                            </View>
                            <View
                                style={{
                                    flex: 1,
                                    flexDirection: "row",
                                    justifyContent: "center",
                                    alignItems: "center"
                                }}
                            >
                                <Button
                                    disabled={!enableVisioRdv}
                                    onPress={joinRoom}
                                    text={"Commencer"}
                                    backgroundColor={baseColors.green.primary}
                                    labelColor={"white"}
                                    style={{ flex: 0.7 }}
                                />
                            </View>
                        </>
                    )}
                </View>
            ) : (
                <>
                    <OpenSansText color={props.color} fontSize={14}>
                        Inscrivez le mail sur lequel vous souhaitez recevoir le lien pour l'entretien.
                    </OpenSansText>

                    <Spacer height={40} />

                    <InputGroup
                        value={event?.customLocation}
                        label={"Adresse mail"}
                        placeholder={"Entrez votre adresse mail"}
                        onChange={(text) => setEvent((old) => ({ ...old, customLocation: text }))}
                    />

                    {Object.keys(freeslots).length > 0 && (
                        <>
                            <Spacer height={40} />
                            <OpenSansText color={props.color} fontSize={14}>
                                Sélectionnez un créneau horaire parmi les prochaines disponibilités :
                            </OpenSansText>
                        </>
                    )}

                    <Spacer height={20} />

                    {isLoading ? (
                        <ActivityIndicator color={baseColors.green.primary} />
                    ) : (
                        <>
                            {noFreeslotAvailable ? (
                                <CustomEmptyData top={10} label="Pas de créneau disponible." />
                            ) : (
                                freeslots?.map((freeslotItemByDate, i) => {
                                    const date = new Date(freeslotItemByDate?.day)
                                    const dateNumber = +date.getTime()
                                    return (
                                        <View key={i}>
                                            <Spacer height={20} />
                                            <TouchableOpacity onPress={() => {}} activeOpacity={0.7}>
                                                <OpenSansText
                                                    fontWeight="bold"
                                                    color={baseColors.green.dark}
                                                    fontSize={16}
                                                >
                                                    {capitalizeFirstLetter(
                                                        moment(freeslotItemByDate?.day).format("dddd D MMMM YYYY")
                                                    )}
                                                </OpenSansText>
                                            </TouchableOpacity>

                                            <Spacer height={20} />

                                            {freeslotItemByDate?.freeslots?.map((freeslotItemByDoctor, index) => {
                                                return (
                                                    <View key={index}>
                                                        <OpenSansText
                                                            style={{ marginLeft: 20, fontWeight: "bold" }}
                                                            color={baseColors.orange.primary}
                                                            fontSize={14}
                                                        >
                                                            {fullName(
                                                                freeslotItemByDoctor?.soignantFirstName,
                                                                freeslotItemByDoctor?.soignantLastName
                                                            ) ?? ""}
                                                        </OpenSansText>

                                                        <Spacer height={20} />

                                                        <FreeTimeSelector
                                                            disabled={disabled}
                                                            selectedFreeslot={selectedFreeslot}
                                                            setSelectedFreeslot={setSelectedFreeslot}
                                                            setActiveSlot={setActiveSlot}
                                                            parentIndex={
                                                                dateNumber + "" + freeslotItemByDoctor?.soignantId
                                                            }
                                                            data={freeslotItemByDoctor?.soignantsFreeslots}
                                                        />

                                                        {activeSlot &&
                                                            showDetails(
                                                                freeslotItemByDate?.day,
                                                                freeslotItemByDoctor?.soignantId
                                                            ) && (
                                                                <View
                                                                    style={{
                                                                        backgroundColor: baseColors.orange.light,
                                                                        padding: 20,
                                                                        paddingVertical: 10
                                                                    }}
                                                                >
                                                                    {specialityName && (
                                                                        <View
                                                                            style={{
                                                                                flexDirection: "row",
                                                                                alignItems: "center",
                                                                                marginVertical: 10
                                                                            }}
                                                                        >
                                                                            <BreeSerifText
                                                                                style={{
                                                                                    color: baseColors.green.primary,
                                                                                    fontSize: 16
                                                                                }}
                                                                            >
                                                                                Profession :
                                                                            </BreeSerifText>

                                                                            <OpenSansText
                                                                                style={{
                                                                                    paddingLeft: 10,
                                                                                    fontSize: 14
                                                                                }}
                                                                            >
                                                                                {specialityName ?? "(Non renseigné"}
                                                                            </OpenSansText>
                                                                        </View>
                                                                    )}

                                                                    {consultationType && (
                                                                        <View
                                                                            style={{
                                                                                flexDirection: "row",
                                                                                alignItems: "center",
                                                                                marginVertical: 10
                                                                            }}
                                                                        >
                                                                            <BreeSerifText
                                                                                style={{
                                                                                    color: baseColors.green.primary,
                                                                                    fontSize: 16
                                                                                }}
                                                                            >
                                                                                Type de consultation :
                                                                            </BreeSerifText>

                                                                            <OpenSansText
                                                                                style={{
                                                                                    paddingLeft: 10,
                                                                                    fontSize: 14
                                                                                }}
                                                                            >
                                                                                {consultationType ?? "(Non renseigné"}
                                                                            </OpenSansText>
                                                                        </View>
                                                                    )}

                                                                    {slotsTime && (
                                                                        <View
                                                                            style={{
                                                                                flexDirection: "row",
                                                                                alignItems: "center",
                                                                                marginVertical: 10
                                                                            }}
                                                                        >
                                                                            <BreeSerifText
                                                                                style={{
                                                                                    color: baseColors.green.primary,
                                                                                    fontSize: 16
                                                                                }}
                                                                            >
                                                                                Créneau de :
                                                                            </BreeSerifText>

                                                                            <OpenSansText
                                                                                style={{
                                                                                    paddingLeft: 10,
                                                                                    fontSize: 14
                                                                                }}
                                                                            >
                                                                                {slotsTime} minutes
                                                                            </OpenSansText>
                                                                        </View>
                                                                    )}
                                                                </View>
                                                            )}

                                                        <Divider
                                                            style={{
                                                                height: 1.5,
                                                                marginVertical: 15,
                                                                backgroundColor: baseColors.orange.primary,
                                                                width: "90%",
                                                                alignSelf: "center"
                                                            }}
                                                        />
                                                    </View>
                                                )
                                            })}
                                        </View>
                                    )
                                })
                            )}
                        </>
                    )}

                    <Button
                        text="Créer le rendez-vous"
                        disabled={disableBtn || fetchingUserInfos}
                        onPress={() => performCreateEvent(isSimpleRdv)}
                        backgroundColor={baseColors.green.primary}
                        labelColor="white"
                        loading={saving}
                    />

                    {saving && progressMessage && (
                        <OpenSansText style={{ marginTop: 20 }} fontSize={RFValue(16)} textAlign={"center"}>
                            {progressMessage + " ..."}
                        </OpenSansText>
                    )}

                    {showDatepicker && (
                        <DateTimePicker
                            testID="dateTimePicker"
                            value={date}
                            mode={"date"}
                            is24Hour={true}
                            display="default"
                            onChange={onChangeDateTimePicker}
                        />
                    )}
                </>
            )}
        </View>
    )
}
